import React from 'react';
import Highlight from 'react-highlighter';
import { RootStateOrAny, useSelector } from 'react-redux';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { getInstructions } from '../actions/initialActions';
import InstructionIcon from './InstructionIcon';
import InstructionLegend from './InstructionLegend';


interface SubCategory {
    name: string,
    instructionTypes: Array<string>,
    level: string,
    id: string,
    code: string
}

const TabularDataModal = (props: { modal: boolean, toggleModal: Function }) => {
    const tabularQuery = useSelector((state: RootStateOrAny) => state.tabularQuery)
    const tabularData = useSelector((state: RootStateOrAny) => state.tabularData)
    const fy = useSelector((state: RootStateOrAny) => state.fy)

    const toggle = () => props.toggleModal();

    const instructionIcons = (instructionTypes: Array<string>, id: string, code: string, node: string) => {
        return (
            <span className="pull-right">
                <span className="instruction-icon">
                    {instructionTypes.includes('note') ? <InstructionIcon id={`note-${id}`} title={`Notes: ${code}`} retrieveData={() => getInstructions(code, node, 'note', fy)} className="icon-note" /> : ''}
                    {instructionTypes.includes('include') ? <InstructionIcon id={`include-${id}`} title={`Include: ${code}`} retrieveData={() => getInstructions(code, node, 'include', fy)} className="icon-include" /> : ''}
                    {instructionTypes.includes('addcode') ? <InstructionIcon id={`add-code-${id}`} title={`Use Additional Code: ${code}`} retrieveData={() => getInstructions(code, node, 'addcode', fy)} className="icon-add-code" /> : ''}
                    {instructionTypes.includes('codefirst') ? <InstructionIcon id={`code-first-${id}`} title={`Use Code First: ${code}`} retrieveData={() => getInstructions(code, node, 'codefirst', fy)} className="icon-code-first" /> : ''}
                    {instructionTypes.includes('codealso') ? <InstructionIcon id={`code-also-${id}`} title={`Use Code Also: ${code}`} retrieveData={() => getInstructions(code, node, 'codealso', fy)} className="icon-code-also" /> : ''}
                    {instructionTypes.includes('seventhchar') ? <InstructionIcon id={`seventh-char-${id}`} title={`Use Seventh Character: ${code}`} retrieveData={() => getInstructions(code, node, 'seventhchar', fy)} className="icon-seventh-char" /> : ''}
                    {instructionTypes.includes('excludes1') ? <InstructionIcon id={`excludes1-${id}`} title={`Excludes1: ${code}`} retrieveData={() => getInstructions(code, node, 'excludes1', fy)} className="icon-excludes1" /> : ''}
                    {instructionTypes.includes('excludes2') ? <InstructionIcon id={`excludes2-${id}`} title={`Excludes: ${code}`} retrieveData={() => getInstructions(code, node, 'excludes2', fy)} className="icon-excludes2" /> : ''}
                </span>
            </span>
        )
    }

    const isLowestSubCategoryLevel = (level: string, index: number) => {
        var numSubcats = tabularData.subcategories.length;
        var isLastItem = (index === numSubcats - 1);
        var nextItem = {'level': '0'};
        if (!isLastItem) {
            nextItem = tabularData.subcategories[index+1];
        }
        if (level === '3') {
            // level 3 is always last leaf in tree
            return true;
        } else if (level === '2' && (isLastItem || nextItem.level === '2' || nextItem.level === '1')) {
            // if we're the last item, next item is same level, or goes back up a level, this is last leaf
            return true;
        } else if (level === '1' && (isLastItem || nextItem.level === '1')) {
            // if we're the last item, next item is same level, this is last leaf
            return true;
        }
        return false;
    };

    const rowData = tabularData ? tabularData.subcategories.map((subCategory: SubCategory, index: number) => {
        const subCategoryNameContainsQuery = subCategory.name.includes(tabularQuery + ' ');

        return (
            <li key={subCategory.id} className={`result-success list-unstyled level${subCategory.level} ${isLowestSubCategoryLevel(subCategory.level, index) ? 'strong' : ''}`}>
                <div className="d-flex justify-content-between">
                    <div>
                        {subCategoryNameContainsQuery ? <Highlight matchElement="span" matchClass="highlighted-text" search={subCategory.name}>{subCategory.name}</Highlight> : subCategory.name}
                    </div>
                    <div>
                        {instructionIcons(subCategory.instructionTypes, subCategory.id, subCategory.code, 'category')}
                    </div>
                </div>
            </li>
        )
    }) : null

    return (
        <div>
            <Modal isOpen={props.modal} toggle={toggle} className='modal-xl'>
                <ModalHeader toggle={toggle}>
                    <div className="modal-main-title">Tabular List of Diseases: {tabularQuery}</div>
                    <div className="icon-legend-wrapper">
                        <div className="icon-legend">
                            <div className="legend-title">
                                <div className="title-container">
                                    <span className="sr-only">Icon </span>Legend
                                </div>
                            </div>
                            <ul className="list-inline">
                                <li className="list-inline-item">
                                    <InstructionLegend id="note-legend" title="Notes" className="icon-note" bodyItems={legends.note.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="include-legend" title="Includes" className="icon-include" bodyItems={legends.include.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="add-code-legend" title="Use Additional Code" className="icon-add-code" bodyItems={legends.addcode.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="code-first-legend" title="Code First" className="icon-code-first" bodyItems={legends.codefirst.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="code-also-legend" title="Code Also" className="icon-code-also" bodyItems={legends.codealso.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="seventh-char-legend" title="Seventh Character" className="icon-seventh-char" bodyItems={legends.seventhchar.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="excludes1-legend" title="Excludes1" className="icon-excludes1" bodyItems={legends.excludes1.description} />
                                </li>
                                <li className="list-inline-item">
                                    <InstructionLegend id="excludes2-legend" title="Excludes2" className="icon-excludes2" bodyItems={legends.excludes2.description} />
                                </li>
                            </ul>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody>
                    { tabularData ?
                    <div id="diseases-injuries">
                        <div className="container">
                            <div className="tabular-data-chapter-title d-flex justify-content-between">
                                <div>
                                    {tabularData ? tabularData.chapter.title : ''}
                                </div>
                                <div>
                                    {tabularData ? instructionIcons(tabularData.chapter.instruction_types, 'chapter', tabularData.chapter.title, 'chapter') : ''}
                                </div>
                            </div>
                            <div className="d-flex justify-content-between">
                                <div className="tabular-data-section-title">
                                    {tabularData ? tabularData.section.title : ''}
                                </div>
                                <div>
                                    {tabularData ? instructionIcons(tabularData.section.instruction_types, 'section', tabularData.section.title, 'section') : ''}
                                </div>
                            </div>
                        </div>
                        <ul className="container">
                            <li className="result-success list-unstyled level0">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        {tabularData ? tabularData.three_digit_category.title : ''}
                                    </div>
                                    <div>
                                        {tabularData ? instructionIcons(tabularData.three_digit_category.instruction_types, 'threedigitcategory', tabularData.three_digit_category.code, 'category') : ''}
                                    </div>
                                </div>
                            </li>
                            {rowData}
                        </ul>
                    </div>
                    :
                    <div className="font-weight-bold text-center">Code not found</div>
                    }
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" color="primary" onClick={toggle}>Close</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

const legends = {
    note: {
        icon: 'notes',
        title: 'Notes',
        description: []
    },
    include: {
        icon: 'includes',
        title: 'Includes',
        description: ["The word 'Includes' appears immediately under certain categories to further define, or give examples of, the content of the category."]
    },
    addcode: {
        icon: 'use-additional-code',
        title: 'Use Additional Code',
        description: [
            "Certain conditions have both an underlying etiology and multiple body system manifestations due to the underlying etiology. For such conditions the ICD-10-CM has a coding convention that requires the underlying condition be sequenced first followed by the manifestation. Wherever such a combination exists there is a 'use additional code' note at the etiology code, and a 'code first' note at the manifestation code. These instructional notes indicate the proper sequencing order of the codes, etiology followed by manifestation.", "In most cases the manifestation codes will have in the code title, 'in diseases classified elsewhere.' Codes with this title are a component of the etiology/ manifestation convention. The code title indicates that it is a manifestation code. 'In diseases classified elsewhere' codes are never permitted to be used as first listed or principal diagnosis codes. They must be used in conjunction with an underlying condition code and they must be listed following the underlying condition."
        ]
    },
    codefirst: {
        icon: 'code-first',
        title: 'Code First',
        description: [
            "Certain conditions have both an underlying etiology and multiple body system manifestations due to the underlying etiology. For such conditions the ICD-10-CM has a coding convention that requires the underlying condition be sequenced first followed by the manifestation. Wherever such a combination exists there is a 'use additional code' note at the etiology code, and a 'code first' note at the manifestation code. These instructional notes indicate the proper sequencing order of the codes, etiology followed by manifestation.",
            "In most cases the manifestation codes will have in the code title, 'in diseases classified elsewhere'. Codes with this title are a component of the etiology/manifestation convention. The code title indicates that it is a manifestation code. 'In diseases classified elsewhere' codes are never permitted to be used as first listed or principal diagnosis codes. They must be used in conjunction with an underlying condition code and they must be listed following the underlying condition."
        ]
    },
    codealso: {
        icon: 'code-also',
        title: 'Code Also',
        description: [
            "A code also note instructs that 2 codes may be required to fully describe a condition but the sequencing of the two codes is discretionary, depending on the severity of the conditions and the reason for the encounter."
        ]
    },
    seventhchar: {
        icon: 'use-7th-character',
        title: 'Use Seventh Character',
        description: [
            "For codes less than 6 characters that require a 7th character a placeholder X should be assigned for all characters less than 6. The 7th character must always be the 7th character of a code."
        ]
    },
    excludes1: {
        icon: 'excludes1',
        title: 'Excludes1',
        description: [
            "A type 1 Excludes note is a pure excludes. It means 'NOT CODED HERE!' An Excludes1 note indicates that the code excluded should never be used at the same time as the code above the Excludes1 note. An Excludes1 is used when two conditions cannot occur together, such as a congenital form versus an acquired form of the same condition."
        ]
    },
    excludes2: {
        icon: 'excludes2',
        title: 'Excludes2',
        description: [
            "A type 2 excludes note represents 'Not included here'. An excludes2 note indicates that the condition excluded is not part of the condition it is excluded from but a patient may have both conditions at the same time. When an Excludes2 note appears under a code it is acceptable to use both the code and the excluded code together."
        ]
    },
    chapterblock: {
        icon: 'chapter',
        title: 'Chapter Info',
        description: []
    }
};

export default TabularDataModal;